import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { OrderType } from 'src/smoothr-web-app-core/enums/OrderType';
import Article from 'src/smoothr-web-app-core/models/Article';
import ArticleGroup from 'src/smoothr-web-app-core/models/ArticleGroup';
import OptionGroup from 'src/smoothr-web-app-core/models/OptionGroup';
import Order from 'src/smoothr-web-app-core/models/Order';
import { OrderUtils } from 'src/smoothr-web-app-core/utils/order-utils';
import {
	calculateMinimumPrice,
	defaultsToArticleOption,
	getBasePrice,
	isFromPriceTag,
	minOfItem,
	numberD,
	numberToCurrency,
	sleep,
} from 'src/smoothr-web-app-core/utils/utils';
import { ValidationUtils } from 'src/smoothr-web-app-core/utils/validation-utils';

@Component({
	selector: 'app-selection-product-from-free-article',
	templateUrl: './selection-product-from-free-article.component.html',
	styleUrls: ['./selection-product-from-free-article.component.scss'],
	standalone: false,
})
export class SelectionProductFromFreeArticleComponent implements OnInit {
	public selectedArticles: { article: Article; quantity: number }[] = [];
	articlesList: Article[];
	order: Order;
	numberToCurrency = numberToCurrency;

	constructor(
		private modalCtrl: ModalController,
		private translate: TranslateService,
		private cdr: ChangeDetectorRef
	) {}

	get showSelectedArticles() {
		return this.selectedArticles;
	}

	static async show(
		modalCtrl: ModalController,
		articlesList: Article[],
		order: Order
	): Promise<{
		data: { article: Article }[];
	}> {
		const modal = await modalCtrl.create({
			component: SelectionProductFromFreeArticleComponent,
			cssClass: ['auto-height, recommendation-dialog'],
			componentProps: {
				articlesList,
				order,
			},
			showBackdrop: true,
		});
		await modal.present();
		await sleep(100);
		const result = (await modal.onDidDismiss()).data;
		return result;
	}

	ngOnInit() {}

	trackBy(index: number, article: Article) {
		return article._id;
	}

	reloadPriceNew(articleValue: Article) {
		const articleGroup = new ArticleGroup();
		articleGroup.article = articleValue;
		articleGroup.groups = defaultsToArticleOption(articleValue, [], [], OrderType.STANDARD, this.order.preorder.type);

		articleGroup.quantity = 1;

		const article = JSON.parse(JSON.stringify(articleGroup.article)) as Article;
		let price = OrderUtils.totalPrice(articleGroup, this.order?.type, this.order.preorder.type);

		// If the article has the fromPrice tag, calculate the minimum possible price
		if (isFromPriceTag(articleValue)) {
			price = calculateMinimumPrice(articleValue, this.order?.type, this.order.preorder.type, price);
		} else {
			const processedGroups: string[] = [];

			article.groups.forEach(grp => {
				if (processedGroups.indexOf(grp._id) >= 0) return;
				if (ValidationUtils.isGroupDependencyFulfilled(articleGroup.article, articleGroup.groups, grp).times < 0) return;
				if (grp.requiredAmount == grp.limit) {
					const minimumItem = minOfItem(grp.articles, opt => numberD(opt.price));

					price += numberD(minimumItem.price) * grp.requiredAmount;
					processedGroups.push(grp._id);
				}
			});
		}
		const tagPrice = this.findTagPrice(articleValue);
		let finalPrice = price - tagPrice;

		return numberToCurrency(finalPrice, this.order.currency);
	}

	dependenciesRegardingOptionArticle(article: Article, groups: OptionGroup[], optionArticle: Article): OptionGroup[] {
		return article.groupDependencies
			.map(gd => {
				if (gd.dependencies.find(dependeci => dependeci.groupArticles.includes(optionArticle._id)) == null) {
					return null;
				} else {
					return groups.find(it => gd.group === it._id);
				}
			})
			.filter(it => !!it);
	}

	dismiss() {
		this.modalCtrl.dismiss();
	}

	selectArticle(article: Article) {
		this.selectedArticles = [];

		this.selectedArticles.push({ quantity: 1, article: article });
	}

	increaseQuantity(article: Article) {
		const foundIndex = this.selectedArticles.findIndex(it => it.article._id === article._id);
		if (foundIndex >= 0) {
			this.selectedArticles[foundIndex].quantity = this.selectedArticles[foundIndex].quantity + 1;
		}
	}

	showAmount(article: Article) {
		return this.selectedArticles.find(it => it.article._id == article._id)?.quantity ?? 0;
	}

	decreaseQuantity(article: Article) {
		const foundIndex = this.selectedArticles.findIndex(it => it.article._id === article._id);
		if (foundIndex >= 0) {
			if (this.selectedArticles[foundIndex].quantity === 1) {
				this.selectedArticles = this.selectedArticles.filter(it => it.article._id !== article._id);
				this.cdr.detectChanges();
			} else {
				this.selectedArticles[foundIndex].quantity = this.selectedArticles[foundIndex].quantity - 1;
			}
		}
	}

	checkMeasurmentPlusPfand(article: Article) {
		let result = '';

		const pfandValue = this.checkPfandTag(article);
		if (pfandValue) {
			result =
				this.translate.instant('pfand_text.zzgl') +
				(pfandValue === 'deposit15' ? numberToCurrency(0.15, this.order.currency) : numberToCurrency(0.25, this.order.currency)) +
				this.translate.instant('pfand_text.pfand') +
				'<br/>';
		} else {
			if (result.length > 0) {
				result = result + ')';
			}
		}
		if (article?.measurement && article?.measurement?.unit && article?.measurement?.amount) {
			result =
				result +
				' ' +
				article.measurement?.refAmount +
				' ' +
				article.measurement?.refUnit +
				' = ' +
				this.numberToCurrency(getBasePrice(article, OrderType.STANDARD, this.order.preorder.type), this.order.currency);
		}
		return result;
	}

	checkPfandTag(article: Article) {
		if (article.tags?.length === 0) {
			return '';
		}
		const deposit25 = article.tags.find(it => it.identifier === 'deposit25') ? 'deposit25' : '';
		const deposit15 = article.tags.find(it => it.identifier === 'deposit15') ? 'deposit15' : '';

		return deposit15 || deposit25;
	}

	addArticlesToBasket() {
		this.modalCtrl.dismiss({
			data: this.selectedArticles,
		});
	}

	isFromPrice(article: Article): boolean {
		return isFromPriceTag(article);
	}

	findTagPrice(article: Article) {
		if (article && article.tags && article.tags.length > 0 && article.tags.find(it => it.identifier.includes('deposit'))) {
			const foundTag = article?.tags?.find(
				it =>
					it?.identifier === 'deposit25' ||
					it?.identifier === 'deposit15' ||
					it?.identifier === 'deposit08' ||
					it?.identifier === 'deposit28'
			);
			if (foundTag) {
				if (foundTag.identifier === 'deposit25') {
					return 0.25;
				}
				if (foundTag.identifier === 'deposit15') {
					return 0.15;
				}
				if (foundTag.identifier === 'deposit08') {
					return 0.08;
				}
				if (foundTag.identifier === 'deposit28') {
					return 0.28;
				}
				return 0;
			}
			return null;
		} else if (article?.deposit) {
			return numberD(article.deposit);
		} else {
			return null;
		}
	}
}
